import type { ProjectConfig, ServiceItem, TeamMember, PropertyItem, NewsItem } from '../types';

export interface MockupOptions {
  projectConfig: ProjectConfig;
  sections: ('header' | 'hero' | 'features' | 'properties' | 'about' | 'team' | 'contact' | 'footer')[];
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function navItems(config: ProjectConfig): { label: string; href: string }[] {
  const items: { label: string; href: string }[] = [{ label: '首頁', href: '#' }];
  if (config.pages.about) items.push({ label: '關於我們', href: '#about' });
  if (config.pages.services) items.push({ label: '服務', href: '#services' });
  if (config.pages.properties) items.push({ label: '物業', href: '#properties' });
  if (config.pages.contact) items.push({ label: '聯絡我們', href: '#contact' });
  return items;
}

function generateHeader(config: ProjectConfig): string {
  const items = navItems(config);
  return `
    <header class="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-sm">
      <nav class="container mx-auto px-4 py-4 flex justify-between items-center">
        <a href="#" class="text-xl font-bold" style="color: var(--pf-primary)">${escapeHtml(config.brand.nameCn)}</a>
        <div class="hidden md:flex gap-6 text-sm">
          ${items.map(item => `<a href="${item.href}" class="hover:opacity-80">${escapeHtml(item.label)}</a>`).join('')}
        </div>
        <button class="md:hidden p-2 rounded" style="background: var(--pf-primary); color: white">☰</button>
      </nav>
    </header>
  `;
}

function generateHero(config: ProjectConfig): string {
  const bgStyle = config.hero.backgroundType === 'gradient'
    ? `background: linear-gradient(135deg, ${config.design.primaryColor}, ${config.design.secondaryColor});`
    : config.hero.backgroundType === 'image' && config.hero.backgroundValue
      ? `background-image: url('${escapeHtml(config.hero.backgroundValue)}'); background-size: cover; background-position: center;`
      : `background-color: ${config.design.primaryColor};`;

  return `
    <section class="relative py-20 md:py-32 text-white" style="${bgStyle}">
      <div class="container mx-auto px-4 text-center">
        <p class="text-sm md:text-base uppercase tracking-widest mb-4 opacity-90">${escapeHtml(config.hero.tagline)}</p>
        <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
          ${escapeHtml(config.hero.titleLine1)}<br/>${escapeHtml(config.hero.titleLine2)}
        </h1>
        <p class="text-base md:text-lg max-w-2xl mx-auto mb-8 opacity-90">${escapeHtml(config.hero.description)}</p>
        <div class="flex flex-col md:flex-row gap-4 justify-center">
          <a href="#" class="px-6 py-3 rounded font-semibold" style="background: var(--pf-accent); color: white">${escapeHtml(config.hero.primaryButtonText)}</a>
          <a href="#" class="px-6 py-3 rounded font-semibold border border-white/50">${escapeHtml(config.hero.secondaryButtonText)}</a>
        </div>
      </div>
    </section>
  `;
}

function generateFeatures(config: ProjectConfig): string {
  if (!config.services.length) return '';
  return `
    <section id="services" class="py-16 md:py-24 bg-white dark:bg-slate-900">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <p class="text-sm uppercase tracking-widest mb-2" style="color: var(--pf-primary)">我們的服務</p>
          <h2 class="text-2xl md:text-4xl font-bold">專業服務</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          ${config.services.map((service: ServiceItem) => `
            <div class="p-6 rounded-lg border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
              <div class="w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white" style="background: var(--pf-primary)">
                <span class="text-xl">${escapeHtml(service.icon)}</span>
              </div>
              <h3 class="text-lg font-semibold mb-2">${escapeHtml(service.title)}</h3>
              <p class="text-sm text-slate-600 dark:text-slate-300">${escapeHtml(service.description)}</p>
            </div>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function formatPrice(price: number, unit: string): string {
  return `HK$ ${price.toLocaleString()}${unit ? `/${unit}` : ''}`;
}

function generateProperties(config: ProjectConfig): string {
  const properties = config.properties ?? [];
  const featured = properties.filter((p: PropertyItem) => p.featured).slice(0, 3);
  if (!featured.length) return '';
  return `
    <section id="properties" class="py-16 md:py-24 bg-slate-50 dark:bg-slate-800">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <p class="text-sm uppercase tracking-widest mb-2" style="color: var(--pf-primary)">精選物業</p>
          <h2 class="text-2xl md:text-4xl font-bold">推薦物業</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          ${featured.map((property: PropertyItem) => `
            <div class="rounded-lg overflow-hidden bg-white dark:bg-slate-900 shadow-sm hover:shadow-lg transition-shadow">
              <div class="h-48 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400">
                ${property.images.length ? `<img src="${escapeHtml(property.images[0])}" alt="" class="w-full h-full object-cover"/>` : '物業圖片'}
              </div>
              <div class="p-5">
                <div class="text-sm mb-1" style="color: var(--pf-primary)">${escapeHtml(property.category)}</div>
                <h3 class="text-lg font-semibold mb-2">${escapeHtml(property.title)}</h3>
                <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">${escapeHtml(property.description)}</p>
                <div class="font-bold text-lg" style="color: var(--pf-accent)">${formatPrice(property.price, property.priceUnit)}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function generateAbout(config: ProjectConfig): string {
  if (!config.pages.about) return '';
  return `
    <section id="about" class="py-16 md:py-24 bg-white dark:bg-slate-900">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div class="h-64 md:h-80 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400">
            ${config.brand.logoUrl ? `<img src="${escapeHtml(config.brand.logoUrl)}" alt="" class="w-full h-full object-contain p-4"/>` : '公司圖片'}
          </div>
          <div>
            <p class="text-sm uppercase tracking-widest mb-2" style="color: var(--pf-primary)">關於我們</p>
            <h2 class="text-2xl md:text-4xl font-bold mb-4">${escapeHtml(config.brand.nameCn)}</h2>
            <p class="text-slate-600 dark:text-slate-300 mb-6">${escapeHtml(config.brand.description)}</p>
            <div class="flex flex-wrap gap-2">
              ${['市場分析', '投資建議', '銀行估價', '按揭轉介'].map(tag => `
                <span class="px-3 py-1 text-sm rounded-full border border-slate-200 dark:border-slate-700">${escapeHtml(tag)}</span>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    </section>
  `;
}

function generateTeam(config: ProjectConfig): string {
  if (!config.team.length) return '';
  return `
    <section class="py-16 md:py-24 bg-slate-50 dark:bg-slate-800">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <p class="text-sm uppercase tracking-widest mb-2" style="color: var(--pf-primary)">專業團隊</p>
          <h2 class="text-2xl md:text-4xl font-bold">銷售團隊</h2>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          ${config.team.map((member: TeamMember) => `
            <div class="text-center p-4 bg-white dark:bg-slate-900 rounded-lg shadow-sm">
              <div class="w-20 h-20 mx-auto rounded-full bg-slate-200 dark:bg-slate-700 mb-3 flex items-center justify-center text-slate-400">
                ${member.photo ? `<img src="${escapeHtml(member.photo)}" alt="" class="w-full h-full object-cover rounded-full"/>` : '頭像'}
              </div>
              <h3 class="font-semibold">${escapeHtml(member.name)}</h3>
              <p class="text-sm text-slate-600 dark:text-slate-300">${escapeHtml(member.title)}</p>
            </div>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function generateContact(config: ProjectConfig): string {
  if (!config.pages.contact) return '';
  return `
    <section id="contact" class="py-16 md:py-24 bg-white dark:bg-slate-900">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p class="text-sm uppercase tracking-widest mb-2" style="color: var(--pf-primary)">聯絡我們</p>
            <h2 class="text-2xl md:text-4xl font-bold mb-6">與我們聯繫</h2>
            <div class="space-y-4 text-slate-600 dark:text-slate-300">
              <p>電話：<a href="tel:${escapeHtml(config.contact.phone)}" class="underline">${escapeHtml(config.contact.phoneDisplay || config.contact.phone)}</a></p>
              <p>電郵：<a href="mailto:${escapeHtml(config.contact.email)}" class="underline">${escapeHtml(config.contact.email)}</a></p>
              <p>地址：${escapeHtml(config.contact.addressDisplay || config.contact.address)}</p>
              <p>服務時間：${escapeHtml(config.contact.businessHours)}</p>
            </div>
          </div>
          <div class="h-64 md:h-auto rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400">
            地圖區域
          </div>
        </div>
      </div>
    </section>
  `;
}

function generateFooter(config: ProjectConfig): string {
  const items = navItems(config);
  return `
    <footer class="py-10 text-white" style="background: var(--pf-dark)">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <h3 class="text-lg font-bold mb-2">${escapeHtml(config.brand.nameCn)}</h3>
            <p class="text-sm opacity-80">${escapeHtml(config.brand.description.slice(0, 120))}...</p>
          </div>
          <div>
            <h4 class="font-semibold mb-2">快速連結</h4>
            <ul class="space-y-1 text-sm opacity-80">
              ${items.map(item => `<li><a href="${item.href}" class="hover:underline">${escapeHtml(item.label)}</a></li>`).join('')}
            </ul>
          </div>
          <div>
            <h4 class="font-semibold mb-2">聯絡資訊</h4>
            <p class="text-sm opacity-80">${escapeHtml(config.contact.phoneDisplay || config.contact.phone)}</p>
            <p class="text-sm opacity-80">${escapeHtml(config.contact.email)}</p>
            <p class="text-sm opacity-80">牌照號碼：${escapeHtml(config.brand.licenseNumber || '')}</p>
          </div>
        </div>
        <div class="border-t border-white/20 pt-6 text-center text-sm opacity-70">
          © ${new Date().getFullYear()} ${escapeHtml(config.brand.nameCn)}. All rights reserved.
        </div>
      </div>
    </footer>
  `;
}

const sectionGenerators: Record<string, (config: ProjectConfig) => string> = {
  header: generateHeader,
  hero: generateHero,
  features: generateFeatures,
  properties: generateProperties,
  about: generateAbout,
  team: generateTeam,
  contact: generateContact,
  footer: generateFooter,
};

export function generateMockup(options: MockupOptions): string {
  const { projectConfig, sections } = options;
  const bodyContent = sections
    .map(section => sectionGenerators[section]?.(projectConfig) || '')
    .join('\n');

  const isDark = projectConfig.design.darkMode;

  return `<!DOCTYPE html>
<html lang="${projectConfig.language}" class="${isDark ? 'dark' : ''}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(projectConfig.displayName)} — Mockup</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap" rel="stylesheet">
  <style>
    :root {
      --pf-primary: ${projectConfig.design.primaryColor};
      --pf-secondary: ${projectConfig.design.secondaryColor};
      --pf-accent: ${projectConfig.design.accentColor};
      --pf-bg: ${projectConfig.design.backgroundColor};
      --pf-text: ${projectConfig.design.textColor};
      --pf-dark: #0f172a;
    }
    body { font-family: 'Inter', 'Noto Sans TC', system-ui, sans-serif; }
  </style>
  <script>
    tailwind.config = { darkMode: 'class' };
  </script>
</head>
<body class="bg-[var(--pf-bg)] text-[var(--pf-text)] min-h-screen">
  ${bodyContent}
  <div class="fixed bottom-4 right-4 z-[9999] px-4 py-2 rounded-full text-xs font-semibold shadow-lg" style="background: var(--pf-dark); color: white; opacity: 0.85;">
    Mockup 預覽 — 非最終網站
  </div>
</body>
</html>`;
}
